import { useState, useEffect } from 'react';
import { Music, Eye, TrendingUp, Globe } from 'lucide-react';
import AdminShell from '../components/AdminShell';
import { supabase } from '../lib/supabaseClient';
import InfoTooltip from '../components/InfoTooltip';

type TimePeriod = '30d' | 'all';

interface SongEngagement {
  totalExplores: number;
  uniqueExplorers: number;
}

interface ReactionStats {
  moved: number;
  empowered: number;
  curious: number;
  heavy: number;
}

interface TopSong {
  id: string;
  title: string;
  artist: string;
  explores: number;
  collections: number;
  reactions: number;
}

interface ModalEngagement {
  look_deeper: number;
  one_key_insight: number;
  explore_connections: number;
  take_it_further: number;
}

interface TopicEngagement {
  topic: string;
  count: number;
}

interface CountryBreakdown {
  country_code: string;
  country_name: string;
  event_count: number;
  percentage: number;
}

export default function AdminAnalytics() {
  const [error, setError] = useState<string | null>(null);
  const [timePeriod, setTimePeriod] = useState<TimePeriod>(() => {
    const params = new URLSearchParams(window.location.search);
    const range = params.get('range');
    return (range === '30d' || range === 'all') ? range : '30d';
  });
  const [songEngagement, setSongEngagement] = useState<SongEngagement>({
    totalExplores: 0,
    uniqueExplorers: 0,
  });
  const [reactions, setReactions] = useState<ReactionStats>({
    moved: 0,
    empowered: 0,
    curious: 0,
    heavy: 0,
  });
  const [topSongs, setTopSongs] = useState<TopSong[]>([]);
  const [modalEngagement, setModalEngagement] = useState<ModalEngagement>({
    look_deeper: 0,
    one_key_insight: 0,
    explore_connections: 0,
    take_it_further: 0,
  });
  const [topicEngagement, setTopicEngagement] = useState<TopicEngagement[]>([]);
  const [countryBreakdown, setCountryBreakdown] = useState<CountryBreakdown[]>([]);
  const [countryError, setCountryError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.set('range', timePeriod);
    window.history.replaceState({}, '', `${window.location.pathname}?${params}`);

    // Debug: Check current user and admin status
    supabase.auth.getUser().then(({ data: { user }, error }) => {
      console.log('[Analytics] Current user:', user?.id, user?.email);
      if (error) console.error('[Analytics] Auth error:', error);

      // Check admin status via profile
      if (user) {
        supabase
          .from('profiles')
          .select('role, is_disabled')
          .eq('id', user.id)
          .single()
          .then(({ data, error }) => {
            console.log('[Analytics] User profile:', data);
            if (error) console.error('[Analytics] Profile error:', error);
          });
      }
    });

    loadAnalytics();
  }, [timePeriod]);

  async function loadAnalytics() {
    setLoading(true);
    setError(null);
    try {
      console.log('[Analytics] ========================================');
      console.log('[Analytics] Starting analytics load');
      console.log('[Analytics] Period:', timePeriod);
      console.log('[Analytics] Supabase URL:', supabase.supabaseUrl);

      // Check auth first
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      console.log('[Analytics] Current user before RPC:', user?.id, user?.email);
      if (authError) console.error('[Analytics] Auth error before RPC:', authError);

      const { data, error } = await supabase.rpc('get_admin_analytics_overview', {
        period: timePeriod
      });

      console.log('[Analytics] RPC call completed');
      console.log('[Analytics] Raw error object:', error);
      console.log('[Analytics] Raw data object:', data);
      console.log('[Analytics] Data type:', typeof data);
      console.log('[Analytics] Data stringified:', JSON.stringify(data, null, 2));

      if (error) {
        console.error('[Analytics] ❌ RPC FAILED');
        console.error('[Analytics] Error message:', error.message);
        console.error('[Analytics] Error details:', error.details);
        console.error('[Analytics] Error hint:', error.hint);
        console.error('[Analytics] Error code:', error.code);
        console.error('[Analytics] Full error object:', JSON.stringify(error, null, 2));
        setError(`Failed to load analytics: ${error.message || 'Unknown error'}`);
        setLoading(false);
        return;
      }

      console.log('[Analytics] ✅ RPC SUCCESS');
      console.log('[Analytics] Full data object:', JSON.stringify(data, null, 2));

      if (data) {
        console.log('[Analytics] Processing data...');
        console.log('[Analytics] songs_explored:', data.songs_explored);
        console.log('[Analytics] unique_explorers:', data.unique_explorers);

        setSongEngagement({
          totalExplores: data.songs_explored || 0,
          uniqueExplorers: data.unique_explorers || 0,
        });

        const reactionBreakdown = data.reaction_breakdown || [];
        const reactionStats: ReactionStats = {
          moved: 0,
          empowered: 0,
          curious: 0,
          heavy: 0,
        };

        reactionBreakdown.forEach((r: any) => {
          const key = r.reaction?.toLowerCase();
          console.log('[Analytics] Processing reaction:', key, 'count:', r.count);
          if (key === 'moved' || key === 'empowered' || key === 'curious' || key === 'heavy') {
            reactionStats[key] = r.count || 0;
          }
        });
        console.log('[Analytics] Final reaction stats:', reactionStats);
        setReactions(reactionStats);

        const topSongsByExplores = (data.top_songs_by_explores || []).slice(0, 5);
        const topSongsByCollections = (data.top_songs_by_collections || []).slice(0, 5);

        const topSongIds = new Set([
          ...topSongsByExplores.map((s: any) => s.song_id),
          ...topSongsByCollections.map((s: any) => s.song_id),
        ]);

        const topSongsData: TopSong[] = Array.from(topSongIds).map(songId => {
          const exploreEntry = topSongsByExplores.find((s: any) => s.song_id === songId);
          const collectionEntry = topSongsByCollections.find((s: any) => s.song_id === songId);

          return {
            id: songId as string,
            title: exploreEntry?.title || collectionEntry?.title || 'Unknown',
            artist: exploreEntry?.artist || collectionEntry?.artist || 'Unknown',
            explores: exploreEntry?.count || 0,
            collections: collectionEntry?.count || 0,
            reactions: 0,
          };
        });
        console.log('[Analytics] Top songs data:', topSongsData);
        setTopSongs(topSongsData);

        const modals = data.most_opened_action_modals || [];
        console.log('[Analytics] Modal data:', modals);
        const modalStats: ModalEngagement = {
          look_deeper: 0,
          one_key_insight: 0,
          explore_connections: 0,
          take_it_further: 0,
        };

        modals.forEach((m: any) => {
          console.log('[Analytics] Processing modal:', m.action_modal, 'count:', m.count);
          if (m.action_modal in modalStats) {
            modalStats[m.action_modal as keyof ModalEngagement] = m.count || 0;
          }
        });
        console.log('[Analytics] Final modal stats:', modalStats);
        setModalEngagement(modalStats);

        const topics = (data.most_explored_topics || []).slice(0, 5);
        console.log('[Analytics] Topics data:', topics);
        const topicData = topics.map((t: any) => ({
          topic: t.topic_tag,
          count: t.count,
        }));
        console.log('[Analytics] Processed topic data:', topicData);
        setTopicEngagement(topicData);

        console.log('[Analytics] ✅ All data processed successfully');
      } else {
        console.warn('[Analytics] ⚠️ No data returned from RPC');
      }

      // Load country breakdown
      console.log('[Analytics] ----------------------------------------');
      console.log('[Analytics] Loading country breakdown...');
      const { data: countryData, error: countryError } = await supabase.rpc('get_admin_country_breakdown', {
        period: timePeriod
      });

      if (countryError) {
        console.error('[Analytics] ❌ Country RPC FAILED');
        console.error('[Analytics] Country error:', countryError.message);
        console.error('[Analytics] Country error details:', countryError.details);
        setCountryError(countryError.message || 'Unknown error');
        setCountryBreakdown([]);
      } else {
        console.log('[Analytics] ✅ Country RPC SUCCESS');
        console.log('[Analytics] Country data:', countryData);
        setCountryError(null);
        setCountryBreakdown(countryData || []);
      }
    } catch (err) {
      console.error('[Analytics] ❌ FATAL ERROR:', err);
      console.error('[Analytics] Error type:', typeof err);
      setError(`An unexpected error occurred: ${err instanceof Error ? err.message : 'Unknown error'}`);
      console.error('[Analytics] Error details:', JSON.stringify(err, null, 2));
    } finally {
      console.log('[Analytics] ========================================');
      console.log('[Analytics] Load complete. Setting loading to false');
      setLoading(false);
    }
  }


  const MetricCard = ({ label, value, icon: Icon }: { label: string; value: string | number; icon: typeof Music }) => (
    <div
      className="bg-[#181818] border border-[#282828] rounded-xl p-6"
      style={{
        boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16)',
      }}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="w-10 h-10 bg-[#40E0D0]/10 rounded-lg flex items-center justify-center">
          <Icon className="w-5 h-5 text-[#40E0D0]" strokeWidth={2} />
        </div>
      </div>
      <div className="text-[28px] font-bold text-[#F5F5F5] mb-1">
        {value}
      </div>
      <div className="text-[13px] text-[#A0A0A0]">
        {label}
      </div>
    </div>
  );

  const EmptyState = ({ message }: { message: string }) => (
    <div className="text-center py-12">
      <p className="text-[14px] text-[#808080]">
        {message}
      </p>
    </div>
  );

  const SectionHeader = ({
    children,
    tooltip,
    helperText
  }: {
    children: React.ReactNode;
    tooltip?: string;
    helperText?: string;
  }) => (
    <div className="mb-3">
      <h2 className="text-[11px] uppercase tracking-[0.08em] text-[#808080] font-medium mb-1 flex items-center">
        {children}
        {tooltip && <InfoTooltip content={tooltip} />}
      </h2>
      {helperText && (
        <p className="text-[12px] text-[#606060] leading-relaxed">
          {helperText}
        </p>
      )}
    </div>
  );

  return (
    <AdminShell activeSection="analytics">
      <div className="p-4 sm:p-6 lg:p-8 flex flex-col lg:flex-row gap-6">
        {/* Main Content Column */}
        <div className="w-full max-w-[1120px]">
          <div className="mb-6">
            <h1 className="text-[22px] sm:text-[24px] font-semibold text-[#F5F5F5] mb-1">
              Analytics
            </h1>
            <p className="text-[13px] sm:text-[14px] text-[#A0A0A0]">
              Track resonance and exploration patterns
            </p>
          </div>

          {/* Mobile Time Period Selector */}
          <div className="lg:hidden mb-6">
            <h3 className="text-[13px] font-semibold text-[#F5F5F5] mb-3">Time Period</h3>
            <div className="flex gap-2">
              {[
                { value: '30d' as TimePeriod, label: 'Last 30 days' },
                { value: 'all' as TimePeriod, label: 'All time' },
              ].map((period) => (
                <button
                  key={period.value}
                  onClick={() => setTimePeriod(period.value)}
                  className={`flex-1 px-4 py-2.5 rounded-lg text-[13px] font-medium transition-colors ${
                    timePeriod === period.value
                      ? 'bg-[#40E0D0]/10 text-[#40E0D0] border border-[#40E0D0]/30'
                      : 'bg-[#1A1A1A] border border-[#282828] text-[#A0A0A0]'
                  }`}
                >
                  {period.label}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-[14px] text-red-400">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="text-center py-20">
              <p className="text-[#A0A0A0]">Loading analytics...</p>
            </div>
          ) : (
            <>
              {/* Song Engagement */}
              <div className="mb-5">
                <SectionHeader helperText="Shows how people discover and navigate your content library.">
                  Exploration Activity
                </SectionHeader>
                {songEngagement.totalExplores === 0 && songEngagement.uniqueExplorers === 0 ? (
                  <div className="bg-[#181818] border border-[#282828] rounded-xl p-12 text-center">
                    <p className="text-[14px] text-[#808080]">No exploration activity yet.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <MetricCard
                      label="Songs Explored"
                      value={songEngagement.totalExplores.toLocaleString()}
                      icon={Eye}
                    />
                    <MetricCard
                      label="Unique Explorers"
                      value={songEngagement.uniqueExplorers.toLocaleString()}
                      icon={TrendingUp}
                    />
                  </div>
                )}
              </div>

              {/* Emotional Signals */}
              <div className="mb-5">
                <SectionHeader
                  tooltip="These reactions are directional signals, not a success score. Use them to understand tone and impact."
                  helperText="Reveals which emotional tones invite the most reflection."
                >
                  Emotional Resonance
                </SectionHeader>
                <div className="bg-[#181818] border border-[#282828] rounded-xl p-6">
                  <h3 className="text-[16px] font-semibold text-[#F5F5F5] mb-6">
                    Reaction Breakdown
                  </h3>
                  <div className="space-y-4">
                    {[
                      { label: 'Moved', count: reactions.moved, color: '#52E3C2' },
                      { label: 'Empowered', count: reactions.empowered, color: '#40E0D0' },
                      { label: 'Curious', count: reactions.curious, color: '#7DD3C0' },
                      { label: 'Heavy', count: reactions.heavy, color: '#FF6F61' },
                    ].map((reaction) => {
                      const total = Object.values(reactions).reduce((sum, val) => sum + val, 0);
                      const percentage = total > 0 ? (reaction.count / total) * 100 : 0;

                      return (
                        <div key={reaction.label}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[14px] text-[#F5F5F5]">{reaction.label}</span>
                            <span className="text-[13px] font-medium" style={{ color: reaction.color }}>
                              {reaction.count} ({percentage.toFixed(1)}%)
                            </span>
                          </div>
                          <div className="h-2 bg-[#282828] rounded-full overflow-hidden">
                            <div
                              className="h-full transition-all duration-500"
                              style={{
                                width: `${percentage}%`,
                                backgroundColor: reaction.color,
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Content Performance */}
              <div className="mb-5">
                <SectionHeader helperText="Identifies which songs attract attention and which inspire lasting connection.">
                  Content Performance
                </SectionHeader>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <div className="bg-[#181818] border border-[#282828] rounded-xl p-6">
                    <h3 className="text-[16px] font-semibold text-[#F5F5F5] mb-6">
                      Top Songs by Explores
                    </h3>
                    {topSongs.length === 0 ? (
                      <EmptyState message="Insights will appear as people begin exploring." />
                    ) : (
                      <div className="space-y-4">
                        {topSongs
                          .sort((a, b) => b.explores - a.explores)
                          .map((song, i) => (
                            <div key={song.id} className="flex items-center gap-4">
                              <div className="w-8 h-8 bg-[#282828] rounded flex items-center justify-center text-[13px] font-semibold text-[#A0A0A0]">
                                {i + 1}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-[14px] font-medium text-[#F5F5F5] truncate">
                                  {song.title}
                                </div>
                                <div className="text-[12px] text-[#A0A0A0]">
                                  {song.artist}
                                </div>
                              </div>
                              <div className="text-[14px] font-medium text-[#40E0D0]">
                                {song.explores}
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>

                  <div className="bg-[#181818] border border-[#282828] rounded-xl p-6">
                    <h3 className="text-[16px] font-semibold text-[#F5F5F5] mb-6 flex items-center">
                      Top Songs by Collections
                      <InfoTooltip content="Collections usually signal long-term value. A song can be highly collected even if it's not widely explored." />
                    </h3>
                    {topSongs.length === 0 ? (
                      <EmptyState message="Insights will appear as people begin exploring." />
                    ) : (
                      <div className="space-y-4">
                        {topSongs
                          .sort((a, b) => b.collections - a.collections)
                          .map((song, i) => (
                            <div key={song.id} className="flex items-center gap-4">
                              <div className="w-8 h-8 bg-[#282828] rounded flex items-center justify-center text-[13px] font-semibold text-[#A0A0A0]">
                                {i + 1}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-[14px] font-medium text-[#F5F5F5] truncate">
                                  {song.title}
                                </div>
                                <div className="text-[12px] text-[#A0A0A0]">
                                  {song.artist}
                                </div>
                              </div>
                              <div className="text-[14px] font-medium text-[#40E0D0]">
                                {song.collections}
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Modal Engagement */}
              <div className="mb-5">
                <SectionHeader helperText="Tracks how people choose to engage beyond the surface level.">
                  Depth of Engagement
                </SectionHeader>
                <div className="bg-[#181818] border border-[#282828] rounded-xl p-6">
                  <h3 className="text-[16px] font-semibold text-[#F5F5F5] mb-6 flex items-center">
                    Ways People Chose to Go Deeper
                    <InfoTooltip content="Shows which modal types users open most, indicating depth patterns—not quality." />
                  </h3>
                  {Object.values(modalEngagement).reduce((sum, val) => sum + val, 0) === 0 ? (
                    <EmptyState message="Insights will appear as people begin exploring." />
                  ) : (
                    <div className="space-y-4">
                      {[
                        { type: 'Look Deeper', count: modalEngagement.look_deeper },
                        { type: 'One Key Insight', count: modalEngagement.one_key_insight },
                        { type: 'Explore Connections', count: modalEngagement.explore_connections },
                        { type: 'Take It Further', count: modalEngagement.take_it_further },
                      ].map((modal) => {
                        const total = Object.values(modalEngagement).reduce((sum, val) => sum + val, 0);
                        const percentage = total > 0 ? (modal.count / total) * 100 : 0;

                        return (
                          <div key={modal.type}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-[14px] text-[#F5F5F5]">{modal.type}</span>
                              <span className="text-[13px] font-medium text-[#40E0D0]">
                                {modal.count}
                              </span>
                            </div>
                            <div className="h-2 bg-[#282828] rounded-full overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-[#40E0D0] to-[#52E3C2] transition-all duration-500"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Topic Engagement */}
              {topicEngagement.length > 0 && (
                <div className="mb-5">
                  <SectionHeader
                    tooltip="Counts topics based on songs explored (not just filter clicks). This reflects real interest."
                    helperText="Surfaces which themes and issues resonate most with your community."
                  >
                    Topics People Explore
                  </SectionHeader>
                  <div className="bg-[#181818] border border-[#282828] rounded-xl p-6">
                    <h3 className="text-[16px] font-semibold text-[#F5F5F5] mb-6">
                      Most Explored Topics
                    </h3>
                    <div className="space-y-4">
                      {topicEngagement.map((topic) => {
                        const maxCount = topicEngagement[0].count;
                        const percentage = (topic.count / maxCount) * 100;

                        return (
                          <div key={topic.topic}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-[14px] text-[#F5F5F5]">{topic.topic}</span>
                              <span className="text-[13px] font-medium text-[#40E0D0]">
                                {topic.count}
                              </span>
                            </div>
                            <div className="h-2 bg-[#282828] rounded-full overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-[#40E0D0] to-[#52E3C2] transition-all duration-500"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Audience by Country */}
              <div className="mb-5">
                <SectionHeader
                  tooltip="Based on active song explores from signed-in users who set their region."
                  helperText="Shows where your most engaged audience is located."
                >
                  Audience by Country
                </SectionHeader>
                {countryError ? (
                  <div className="bg-[#181818] border border-[#FF6F61]/30 rounded-xl p-8 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <Globe className="w-10 h-10 text-[#FF6F61]/50" />
                      <p className="text-[14px] text-[#FF6F61]">Error loading country data</p>
                      <p className="text-[12px] text-[#808080] max-w-sm font-mono">
                        {countryError}
                      </p>
                    </div>
                  </div>
                ) : countryBreakdown.length === 0 ? (
                  <div className="bg-[#181818] border border-[#282828] rounded-xl p-8 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <Globe className="w-10 h-10 text-[#40E0D0]/30" />
                      <p className="text-[14px] text-[#808080]">No country data yet</p>
                      <p className="text-[12px] text-[#606060] max-w-sm">
                        Country is only tracked for signed-in users who set a region.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="bg-[#181818] border border-[#282828] rounded-xl p-6">
                    <h3 className="text-[16px] font-semibold text-[#F5F5F5] mb-6">
                      Top 10 Countries
                    </h3>
                    <div className="space-y-4">
                      {countryBreakdown.map((country) => (
                        <div key={country.country_code}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Globe className="w-4 h-4 text-[#40E0D0]" />
                              <span className="text-[14px] text-[#F5F5F5]">{country.country_name}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-[13px] font-medium text-[#40E0D0]">
                                {country.event_count.toLocaleString()}
                              </span>
                              <span className="text-[12px] text-[#808080] min-w-[45px] text-right">
                                {country.percentage.toFixed(1)}%
                              </span>
                            </div>
                          </div>
                          <div className="h-2 bg-[#282828] rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-[#40E0D0] to-[#52E3C2] transition-all duration-500"
                              style={{ width: `${country.percentage}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile note */}
              <div className="lg:hidden mt-8 p-4 bg-[#1A1A1A] border border-[#282828] rounded-xl text-center">
                <p className="text-[12px] text-[#808080]">
                  Best viewed on desktop for full analytics experience
                </p>
              </div>
            </>
          )}
        </div>

        {/* Right Rail - Time Period Filter (Desktop Only) */}
        <div className="hidden lg:block w-[300px] flex-shrink-0">
          <div className="bg-[#1A1A1A] border border-[#282828] rounded-xl p-5 sticky top-8">
            <h3 className="text-[13px] font-semibold text-[#F5F5F5] mb-4">Time Period</h3>
            <div className="space-y-2">
              {[
                { value: '30d' as TimePeriod, label: 'Last 30 days' },
                { value: 'all' as TimePeriod, label: 'All time' },
              ].map((period) => (
                <button
                  key={period.value}
                  onClick={() => setTimePeriod(period.value)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-[13px] transition-colors ${
                    timePeriod === period.value
                      ? 'bg-[#40E0D0]/10 text-[#40E0D0] border border-[#40E0D0]/30'
                      : 'text-[#A0A0A0] hover:text-[#F5F5F5] hover:bg-[#282828]'
                  }`}
                >
                  {period.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AdminShell>
  );
}
